import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { projectInputSchema } from '../../lib/schemas/project.js';
import { PROJECT_STATUSES, type ProjectStatus } from '../../types/project.js';
import type { Visibility } from '../../types/visibility.js';
import { toAppError } from '../../lib/utils/error.js';
import { Spinner } from '../shared/Spinner.js';
import { VisibilityRadio } from './VisibilityRadio.js';
import { useDraft } from './useDraft.js';
import { DraftRestoredBanner } from './DraftRestoredBanner.js';

export interface ProjectDraftState {
  name: string;
  description: string;
  color: string;
  status: ProjectStatus;
  visibility: Visibility;
}

const PROJECT_COLORS = [
  '#b08968', '#7a8b6f', '#6b7a99', '#a07a7a', '#8a7ab0', '#b0a078',
  '#7a99a0', '#9aa07a', '#a09a7a', '#7a7aa0',
];

const INITIAL: ProjectDraftState = {
  name: '',
  description: '',
  color: PROJECT_COLORS[0]!,
  status: '試作',
  visibility: 'private',
};

interface ProjectFormProps {
  onDone: (id: string) => void;
  editId?: string | undefined;
  initialValues?: ProjectDraftState | undefined;
}

export function ProjectForm({ onDone, editId, initialValues }: ProjectFormProps) {
  const isEditing = !!editId;
  const { fbUser } = useAuth();
  const qc = useQueryClient();
  const draftHook = useDraft<ProjectDraftState>('project', INITIAL);
  const [editDraft, setEditDraft] = useState<ProjectDraftState>(initialValues ?? INITIAL);
  const draft = isEditing ? editDraft : draftHook.value;
  const setDraft = isEditing ? setEditDraft : draftHook.set;
  const clearDraft = isEditing ? () => {} : draftHook.clear;
  const wasRestored = isEditing ? false : draftHook.wasRestored;
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof ProjectDraftState>(k: K, v: ProjectDraftState[K]) =>
    setDraft({ ...draft, [k]: v });

  const m = useMutation({
    mutationFn: async () => {
      if (!fbUser) throw new Error('not signed in');
      const parsed = projectInputSchema.parse({
        name: draft.name,
        description: draft.description,
        color: draft.color,
        status: draft.status,
        visibility: draft.visibility,
      });
      const payload = {
        name: parsed.name,
        description: parsed.description,
        color: parsed.color,
        status: parsed.status as ProjectStatus,
        visibility: parsed.visibility as Visibility,
      };
      if (editId) {
        await projectsDb.update(editId, payload);
        return editId;
      }
      return await projectsDb.create(fbUser.uid, payload);
    },
    onSuccess: async (id) => {
      clearDraft();
      await qc.invalidateQueries({ queryKey: ['projects'] });
      await qc.invalidateQueries({ queryKey: ['projects', 'available'] });
      if (editId) {
        await qc.refetchQueries({ queryKey: ['project', editId] });
      }
      onDone(id);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    m.mutate();
  };

  return (
    <form className="compose-form" onSubmit={onSubmit}>
      {!isEditing && (
        <DraftRestoredBanner visible={wasRestored} onDiscard={clearDraft} />
      )}

      <label className="auth-field">
        <span>トピック名</span>
        <input
          required
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          maxLength={100}
          placeholder="例: 学内FAQ検索アプリ"
        />
      </label>

      <label className="auth-field">
        <span>説明 <em className="mono">何のためのトピックか</em></span>
        <textarea
          rows={2}
          value={draft.description}
          onChange={(e) => set('description', e.target.value)}
          maxLength={500}
        />
      </label>

      <div className="auth-field">
        <span>カラー</span>
        <div className="project-color-picker">
          {PROJECT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={'project-color-swatch' + (draft.color === c ? ' is-selected' : '')}
              style={{ background: c }}
              onClick={() => set('color', c)}
              aria-label={`カラー ${c}`}
              aria-pressed={draft.color === c}
            />
          ))}
        </div>
      </div>

      <label className="auth-field">
        <span>ステータス</span>
        <select
          value={draft.status}
          onChange={(e) => set('status', e.target.value as ProjectStatus)}
        >
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      <VisibilityRadio
        value={draft.visibility}
        onChange={(v) => set('visibility', v)}
      />

      {error && <div className="auth-error">{error}</div>}

      <div className="compose-foot">
        <span className="mono compose-draft-hint">
          {isEditing ? '編集モード' : '下書きは自動保存されます'}
        </span>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {m.isPending ? <Spinner /> : isEditing ? '更新' : '作成'}
        </button>
      </div>
    </form>
  );
}
